import { useRef } from 'react';
import { useLogImpression } from '@archipro-website/tracker';
import type { ImpressionType } from '@archipro-website/tracker';
import { cn } from '@rocco/utils/cn';
import { ArticleTile } from '@rocco/ui/tile-article/components/ArticleTile';
import {
    TileOverlayWithFollowSelector,
    TileMobileActionsSlot,
} from '~/modules/moodboards/components';
import { buildTileActionProps } from '~/modules/moodboards/utils/tile-action-props';
import { ArticleTileAdminMenu } from '~/modules-rocco/admin/components/ArticleTileAdminMenu';
import type { ArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';

/**
 * `category` is the /articles/{category} grid (Figma 49:4495) and `directory`
 * the /articles landing grid (Figma 60:2076). Both lock their image to the same
 * 437x524 so every card in a row shows the same picture height; they differ in
 * their column width, their gutter and their row gap.
 */
type EditorialGridLayout = 'category' | 'directory';

interface EditorialArticleGridProps {
    articles: ArticleTileViewModel[];
    layout?: EditorialGridLayout;
    /**
     * Every card the same width, with none promoted to a double. For a row
     * that closes a section rather than continuing its rhythm — the pattern
     * puts a wide card first, which would read as a new turn starting.
     */
    uniform?: boolean;
    /**
     * The `Impression` type each card logs once seen. A listing's cards are
     * directory impressions; a rail closing an article logs its own.
     */
    impressionType?: ImpressionType;
    /** Called when a card is followed, for callers that log their own event. */
    onTileClick?: (article: ArticleTileViewModel) => void;
    className?: string;
    /** Cards without their standfirst: picture, eyebrow and title only. */
    hideStandfirst?: boolean;
    /** Pictures graded onto the editorial palette. */
    graded?: boolean;
    /**
     * Leading cards whose pictures load with the page, at high priority —
     * the browse grids' own convention for the rows a reader lands on. For
     * grids that open at the fold; a grid far down its page leaves this at
     * zero and lets the pictures load on approach.
     */
    eagerCount?: number;
}

/**
 * The listing's margin, wider than the 48px the rest of the site uses: the
 * cards are pulled in off the edges so the full-bleed carousel above them reads
 * as the page's edge and the grid as something set within it.
 *
 * Exported because everything that has to line up with the grid takes it from
 * here — the categories row above it and the feature row between its turns.
 *
 * The other directories' gutter — 48px at the 1920 frame, 16px on a phone —
 * so a reader moving between the libraries finds every one of them on the
 * same edges. Tracks the centred 1920 container above that width, as the
 * shared gutter does, so the grid stays level with the sections around it on
 * a wide screen.
 */
export const DIRECTORY_GUTTER_X =
    'px-4 md:px-[max(3rem,calc((100%_-_1920px)/2_+_3rem))]';

const GRID_LAYOUT: Record<EditorialGridLayout, string> = {
    // 80px margins, four columns, 32px gutter, 79px rows; below md the
    // two-column grid runs the product grid's 8px gutter and the
    // categories' 40px row rhythm.
    category: `gap-x-2 gap-y-10 md:gap-x-[2rem] md:gap-y-[4.9375rem] ${DIRECTORY_GUTTER_X}`,
    // 80px margins, 24px gutter, 100px rows.
    directory: `gap-x-2 gap-y-10 md:gap-x-[1.5rem] md:gap-y-[6.25rem] ${DIRECTORY_GUTTER_X}`,
};

/**
 * Where a double-width card falls in the repeating run.
 *
 * The rhythm is three rows of a four-column grid — double + two singles, two
 * singles + double, then four singles — so it comes round every ten cards, with
 * the wide ones opening the first row and closing the second. Cards are laid in
 * source order and the pattern is positional, so a page of any length keeps the
 * beat and a short final row simply stops.
 */
const RUN_LENGTH = 10;
const WIDE_IN_RUN = new Set([0, 5]);

/**
 * The cards' real rendered widths, for the srcset. A single is a quarter of
 * the 1824 measure less its share of gutter (~23vw, 438px at the 1920 frame)
 * and half the phone's two-column grid; a wide card spans two columns and the
 * gap between (~47vw, 898px), and the phone's full measure. Without these the
 * media falls back to TileMedia's generic estimate, which reads a card as a
 * third of the viewport and fetches the next rendition up.
 */
const NARROW_CARD_SIZES =
    '(min-width: 1921px) 438px, (min-width: 768px) 23vw, 49vw';
const WIDE_CARD_SIZES =
    '(min-width: 1921px) 898px, (min-width: 768px) 47vw, 100vw';

/**
 * The four-up editorial article grid.
 *
 * Each tile wears the platform hover panel unless the host supplies its own
 * overlay, so a picture in the article directory offers what a picture offers
 * anywhere else.
 *
 * `1rem` is 16px at 1920, so the Figma pixel values carry straight over as rem
 * and scale with the viewport below that.
 */
export const EditorialArticleGrid = ({
    articles,
    layout = 'category',
    uniform = false,
    impressionType = 'LibraryItem_Directory',
    onTileClick,
    className,
    hideStandfirst,
    graded,
    eagerCount = 0,
}: EditorialArticleGridProps) => {
    if (articles.length === 0) return null;

    return (
        <div
            className={cn(
                // Two across below md with the wide cards spanning both, so
                // the phone keeps the desktop mosaic's big-then-small beat.
                'grid grid-cols-2 md:grid-cols-4',
                GRID_LAYOUT[layout],
                className,
            )}
        >
            {articles.map((article, index) => {
                const wide = !uniform && WIDE_IN_RUN.has(index % RUN_LENGTH);
                return (
                    <div
                        key={article.id}
                        className={cn(
                            'relative',
                            // A wide card takes both columns at every width —
                            // full-bleed on the phone's two-column grid.
                            wide && 'col-span-2',
                        )}
                    >
                        <ArticleTileAdminMenu article={article}>
                            <ArticleTile
                                model={article}
                                aspectRatio="featured"
                                rounded
                                hideStandfirst={hideStandfirst}
                                graded={graded}
                                // Half the measure, a smaller cut: the phone's
                                // single-column card steps its serif title to
                                // 16px so less of it is cut at three lines;
                                // the spanning card keeps the full 24px.
                                titleClassName={
                                    wide
                                        ? undefined
                                        : 'text-[1rem] leading-[1.3rem] md:text-[1.5rem] md:leading-[2rem]'
                                }
                                overlay={
                                    <TileOverlayWithFollowSelector
                                        {...buildTileActionProps(article)}
                                    />
                                }
                                onClick={
                                    onTileClick
                                        ? () => onTileClick(article)
                                        : undefined
                                }
                                // Two columns and the gutter between them, over
                                // the single card's own image height — so a wide
                                // card is landscape and its picture still lines
                                // up with the row. The picture covers the wider
                                // box from its centre, which is where the
                                // subject of an article photograph sits.
                                mediaClassName={
                                    wide ? 'aspect-[898/524]' : undefined
                                }
                                mediaSizes={
                                    wide ? WIDE_CARD_SIZES : NARROW_CARD_SIZES
                                }
                                eager={index < eagerCount || undefined}
                            />
                        </ArticleTileAdminMenu>
                        <ArticleCardImpression
                            article={article}
                            type={impressionType}
                        />
                        {/* Mobile actions live outside the tile link so a tap
                            on the `•••` can never navigate the tile. */}
                        <TileMobileActionsSlot
                            {...buildTileActionProps(article)}
                        />
                    </div>
                );
            })}
        </div>
    );
};

/**
 * Logs one impression for a card. A marker over the card rather than a ref on
 * it, because the tile is rocco's and takes no ref.
 */
const ArticleCardImpression = ({
    article,
    type,
}: {
    article: ArticleTileViewModel;
    type: ImpressionType;
}) => {
    const markerRef = useRef<HTMLSpanElement>(null);

    useLogImpression({
        ref: markerRef,
        data: {
            itemID: article.id,
            professionalID: article.professional?.id ?? 0,
            type,
        },
    });

    return (
        <span
            ref={markerRef}
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
        />
    );
};
